import React from "react";
import { Button, Form, Spinner, Table } from "react-bootstrap";
import { formatInr } from "../../utils/financeFormat";
import { RichCashflowCharts, RichLineChart } from "../RichChartWidgets";
import AnalyticsLoungeDashboard from "./AnalyticsLoungeDashboard";
import ProfitMonthExplorer from "./ProfitMonthExplorer";

function periodKeyShortLabel(pk) {
  if (!pk || typeof pk !== "string") return pk || "—";
  const [y, m] = pk.split("-");
  if (!y || !m) return pk;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[Number(m) - 1] || m} ${y}`;
}

const PROFIT_PILLS = [
  { key: "summary", label: "Charts & analysis" },
  { key: "months", label: "Month breakdown" },
];

export default function ProfitAnalyticsPanel({
  profitView,
  setProfitView,
  profitWindowMonths,
  setProfitWindowMonths,
  profitBusy,
  onRecompute,
  cashflowChart,
  profitPack,
  partybossLabels = [],
  bookingCounts = [],
  revenues = [],
}) {
  const chartLabels = cashflowChart?.labels?.length ? cashflowChart.labels : partybossLabels;
  const revenueSeries =
    cashflowChart?.revenueRupee?.length
      ? cashflowChart.revenueRupee.map((v) => Number(v) || 0)
      : revenues;

  const recent = [...(profitPack?.months || [])]
    .sort((a, b) => String(b.periodKey).localeCompare(String(a.periodKey)))
    .slice(0, 4)
    .reverse();

  const showCharts = profitView === "summary";

  const charts = showCharts ? (
    <>
      {profitBusy ? (
        <div className="py-4 text-center">
          <Spinner size="sm" animation="border" className="text-light" />
        </div>
      ) : null}
      {chartLabels.length ? (
        <>
          <RichLineChart
            title="Revenue trends"
            subtitle="Money in by month (excl. Party Boss)"
            badge="P&amp;L"
            height={200}
            labels={chartLabels}
            series={[{ key: "revenue", values: revenueSeries }]}
          />
          {partybossLabels.length ? (
            <RichLineChart
              title="Booking count trends"
              subtitle="Active Party Boss bookings"
              badge="Bookings"
              height={200}
              labels={partybossLabels}
              series={[{ key: "bookings", values: bookingCounts }]}
            />
          ) : null}
        </>
      ) : (
        <p className="text-secondary small px-1">Upload data and recompute to see trend charts.</p>
      )}
    </>
  ) : null;

  const toolbar = (
    <>
      <div>
        <Form.Label className="text-secondary small mb-1">Trailing months</Form.Label>
        <Form.Select
          size="sm"
          className="text-bg-dark border-secondary"
          style={{ width: 120 }}
          value={profitWindowMonths}
          onChange={(e) => setProfitWindowMonths(Number(e.target.value))}
        >
          {[12, 18, 24, 36, 48, 60].map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </Form.Select>
      </div>
      <Button size="sm" variant="outline-light" disabled={profitBusy} type="button" onClick={onRecompute}>
        Recompute workbook
      </Button>
    </>
  );

  const mainHeader = (
    <div className="analytics-lounge-main-header mb-3">
      <h4 className="h6 mb-1 text-info">Month-by-month breakdown</h4>
      <p className="text-secondary small mb-0">
        Expand each month. Party Boss is reference-only and excluded from Total in.
      </p>
    </div>
  );

  return (
    <AnalyticsLoungeDashboard
      title="Profit"
      subtitle="Party Boss is reference-only in Money in — not added to totals."
      toolbar={toolbar}
      pillItems={PROFIT_PILLS}
      activePill={profitView}
      onPillSelect={setProfitView}
      charts={charts}
      showCharts={showCharts}
      mainHeader={mainHeader}
    >
      {profitBusy ? <Spinner size="sm" animation="border" className="text-light mb-2" /> : null}
      <ProfitMonthExplorer months={profitPack?.months || []} />

      {showCharts && recent.length >= 2 ? (
        <div className="analytics-lounge-quick-table mt-4">
          <h4 className="h6 text-info mb-2">Last {recent.length} months — quick performance</h4>
          <Table
            striped
            bordered
            hover
            size="sm"
            variant="dark"
            responsive
            className="mb-0 finance-comparison-table admin-console-data-table"
          >
            <thead>
              <tr>
                <th>Month</th>
                <th className="text-end">Money in (excl. Party Boss)</th>
                <th className="text-end">Money out</th>
                <th className="text-end">Net profit</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((row) => (
                <tr key={`profit-snap-${row.periodKey}`}>
                  <td>{row.displayLabel || periodKeyShortLabel(row.periodKey)}</td>
                  <td className="text-end text-info">{formatInr(row.totalRevenueRupee)}</td>
                  <td className="text-end text-warning">{formatInr(row.totalExpenseRupee)}</td>
                  <td className={`text-end fw-semibold ${Number(row.profitRupee) < 0 ? "text-danger" : "text-success"}`}>
                    {formatInr(row.profitRupee)}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      ) : null}

      {showCharts && (profitPack?.insightBullets || []).length ? (
        <div className="analytics-lounge-insights mt-3 p-3 rounded-3">
          <h4 className="h6 text-info mb-2">AI business analysis</h4>
          <ul className="small text-light mb-0">
            {profitPack.insightBullets.map((txt, idx) => (
              <li key={`ins-${idx}`} className="mb-1">
                {txt}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {showCharts && !(profitPack?.insightBullets || []).length && !(profitPack?.months || []).length ? (
        <p className="text-secondary small mt-3 mb-0">
          Upload bank statements, salary Excel, Petpooja &amp; banquet data — analysis appears here.
        </p>
      ) : null}

      {showCharts && chartLabels.length ? (
        <details className="analytics-lounge-full-charts mt-3">
          <summary className="text-secondary small">Full P&amp;L charts (money in / out / costs)</summary>
          <div className="pt-3">
            <RichCashflowCharts chart={cashflowChart} />
          </div>
        </details>
      ) : null}
    </AnalyticsLoungeDashboard>
  );
}
